import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import logo from '../../../images/Logo.png';
import eocean from '../../../images/eocean.png';
import moment from 'moment';
import 'moment-timezone';
import { getUserTimeZone } from '../../../helpers';

// Create styles
const styles = StyleSheet.create({
    page: {
        padding: 20,
        fontFamily: 'Helvetica',
        position: 'relative',
    },
    certificateContainer: {
        padding: 10,
        paddingBottom: 100, // Add space for footer
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 40,
    },
    headerTitle: {
        fontSize: 15,
        fontWeight: 'bold',
        color: '#333',
        textAlign: "start",
    },
    detailsGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 15,
    },
    detailsColumn: {
        width: '30%',
    },
    statusColumn: {
        width: '30%',
        backgroundColor: '#f0f0f0',
        padding: 5,
        borderRadius: 4,
    },
    detailsLabel: {
        fontWeight: 'bold',
        marginBottom: 5,
        fontSize: 10,
    },
    detailsValues: {
        color: 'rgb(24, 23, 23)',
        fontSize: 8,
    },
    messageGrid: {
        marginBottom: 15,
    },
    messageDetails: {
        flexDirection: 'row',
        marginBottom: 10,
    },
    messageValue: {
        marginLeft: 10,
       marginTop: 1,
        color: 'rgb(24, 23, 23)',
        fontSize: 8,
    },
    traceRouteTitle: {
        fontSize: 8,
        fontWeight: 'bold',
        marginBottom: 10,
        marginTop: 20,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: 40,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
        borderBottomStyle: 'solid',
        minHeight: 30,
        alignItems: 'center',
    },
    tableCol1: {
        width: '40%',
        padding: 10,
        fontSize: 8.5,
        color: 'rgb(24, 23, 23)',
    },
    tableCol2: {
        width: '30%',
        padding: 10,
        fontSize: 8.5,
        color: 'rgb(24, 23, 23)',
        textAlign: 'center',
    },
    tableCol3: {
        width: '30%',
        padding: 10,
        fontSize: 8.5,
        color: 'rgb(24, 23, 23)',
        textAlign: 'right',
    },
    secondary: {
        color: '#666',
        marginLeft: 30,
        fontSize: 8,
    },
    noSignature: {
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 50,
        marginBottom: 50,
    },
    noSignatureText: {
        width: '60%',
        textAlign: 'center',
        border: '1pt solid #000',
        padding: '30px 10px',
        color: 'rgb(24, 23, 23)',
        fontSize: 8,
        alignSelf: 'center',
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 10,
    },
    footerDetail: {
        fontSize: 8,
        color: '#666',
    },
    footerValue: {
        fontSize: 8,
        fontWeight: 'bold',
    },
    footerRow: {
        flexDirection: 'row',
        marginBottom: 5,
    },
    spaceRight: {
        marginRight: 5,
    },
});

const NotificationCertificatePDF = ({ history, data }) => {
    const chunkArray = (array, size) => {
        const chunked = [];
        let index = 0;
        while (index < array.length) {
            chunked.push(array.slice(index, index + size));
            index += size;
        }
        return chunked;
    };

    const historyChunks = chunkArray(history, 15);

    const totalPages = historyChunks.filter(chunk => chunk.length > 0).length;

    return (
        <Document>
            {historyChunks.map((chunk, pageIndex) => (
                <Page key={pageIndex} size="A4" style={styles.page}>
                    <View style={styles.certificateContainer}>
                        {/* Only show header on first page */}
                        {pageIndex === 0 && (
                            <>
                                <View style={styles.header}>
                                    <Image src={logo} style={{ width: 80 }} />
                                    <Text style={styles.headerTitle}>Notification Delivery Certificate</Text>

                                    <Image src={`${process.env.REACT_APP_API_URL}${data.logo}`} style={{ width: 80 }} />
                                    {/* Empty view for alignment */}
                                </View>

                                <View style={styles.detailsGrid}>
                                    <View style={styles.statusColumn}>
                                        <Text style={styles.detailsLabel}>Transaction Reference No.</Text>
                                        <Text style={styles.detailsValues}>{data?.message_id}</Text>
                                    </View>

                                    <View style={styles.statusColumn}>
                                        <Text style={styles.detailsLabel}>Delivery Status: <Text style={{ color: data?.Status?.color, fontSize: 8, marginBottom: 5 }}>
                                            {data?.Status?.name}
                                        </Text></Text>
                                        
                                        <Text style={styles.detailsValues}>
                                        <Text style={styles.detailsLabel}>Delivery Time:</Text>
                                            {moment.utc(data?.acceptance?.timestamp).tz(getUserTimeZone()).format('DD/MM/YY | hh:mm:ss A')}
                                        </Text>
                                    </View>
                                </View>

                                <View style={styles.messageGrid}>
                                    <View style={styles.messageDetails}>
                                        <Text style={styles.detailsLabel}>Message Payload:</Text>
                                        <Text style={styles.messageValue}>{data?.message}</Text>
                                    </View>
                                    <View style={styles.messageDetails}>
                                        <Text style={styles.detailsLabel}>System Classification(s):</Text>
                                        <Text style={styles.messageValue}>Secure - Time Sensitive - High Priority</Text>
                                    </View>
                                </View>
                            </>
                        )}

                        {/* Page numbering for subsequent pages */}
                        {pageIndex > 0 && (
                            <View style={{ marginBottom: 20 }}>
                                <Text style={styles.detailsLabel}>
                                    Notification Delivery Certificate - Page {pageIndex + 1}
                                </Text>
                                <Text style={styles.detailsValues}>Continued from previous page</Text>
                            </View>
                        )}

                        {/* Show trace route title on each page */}
                        <Text style={styles.traceRouteTitle}>
                            Trace Route {pageIndex > 0 ? `(Continued - Page ${pageIndex + 1})` : ''}
                        </Text>

                        <View style={styles.table}>
                            {chunk.map((item, index) => (
                                <View key={`${pageIndex}-${index}`} style={styles.tableRow}>

                                    <Text style={styles.tableCol1}>{item?.status}</Text>
                                    <Text style={styles.tableCol2}>{item?.channel || '-'}</Text>
                                    <Text style={styles.tableCol3}>{item?.datetime}</Text>
                                </View>
                            ))}
                        </View>

                        {/* Only show signature on last page */}
                        {pageIndex === historyChunks.length - 1 && (
                            <View style={styles.noSignature}>
                                <Text style={styles.noSignatureText}>
                                    This is a xNotify system generated report & does not require a signature.
                                </Text>
                            </View>
                        )}
                    </View>

                    {/* Footer on every page */}
                    <View style={styles.footer} fixed>
                        <View>
                            {/* <View style={styles.footerRow}>
                                <Text style={[styles.footerDetail, styles.spaceRight]}>Report ID:</Text>
                                <Text style={styles.footerValue}>EON-XN-KU-5515414753-2</Text>
                            </View> */}
                            <View style={styles.footerRow}>
                                <Text style={[styles.footerDetail, styles.spaceRight]}>Generated on</Text>
                                <Text style={styles.footerValue}>
                                    {moment(data?.createdAt).format('DD-MM-YY HH:mm')}
                                    <Text style={styles.footerDetail}>  </Text>
                                </Text>
                            </View>
                            <View style={styles.footerRow}>
                                <Text style={styles.footerDetail}>Page {pageIndex + 1} of {totalPages}</Text>
                            </View>
                        </View>
                        <Image src={eocean} style={{ width: 180 }} />
                    </View>
                </Page>
            ))}
        </Document>
    );
};

export default NotificationCertificatePDF;